import { useEffect, useState } from "react";
import { Loader2, Search, Send, Save, Mail, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { fetchAdminUsers, fetchMyTeam, sendEmail, sendSms, designCreateTemplate, isAdminUser } from "@/lib/api";

// One-click "Email this flyer": pick a CRM customer → preview (branded
// header/footer, dealer-personalised) → send. Also saves to Design Templates.
export default function FlyerEmailDialog({ open, onOpenChange, recId, user, flyer }) {
  const [team, setTeam] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [contact, setContact] = useState(null);
  const [subject, setSubject] = useState(flyer?.subject || "");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSubject(flyer?.subject || "");
  }, [flyer?.subject]);

  // Admins search the whole CRM; dealers pick from their own team (CRM contacts).
  useEffect(() => {
    if (!open || !recId) return;
    let alive = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        let list;
        if (isAdminUser(user)) {
          const res = await fetchAdminUsers({ callerUserRecordId: recId, q, limit: 12 });
          list = res?.users || [];
        } else {
          let tm = team;
          if (!tm) {
            const res = await fetchMyTeam(user);
            tm = (res?.team_members || []).map((m) => ({ ...m, id: m.contact_record_id || m.id }));
            if (alive) setTeam(tm);
          }
          const needle = q.trim().toLowerCase();
          list = tm.filter((m) => !needle || `${m.full_name || ""} ${m.email || ""}`.toLowerCase().includes(needle)).slice(0, 12);
        }
        if (alive) setResults(list.filter((u) => u.email));
      } catch {
        if (alive) setResults([]);
      } finally {
        if (alive) setSearching(false);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, open, recId, user, team]);

  const [texting, setTexting] = useState(false);
  const flyerLink = typeof window !== "undefined" ? window.location.href : "";

  async function sendText() {
    if (!contact) return toast.error("Pick a customer first");
    if (!contact.phone) return toast.error(`${contact.first_name || "This contact"} has no phone number on file`);
    setTexting(true);
    try {
      const who = (user?.first_name || "").trim();
      const res = await sendSms({
        userRecordId: recId,
        message: `Hi {{first_name}}, here's the OreVital ${flyer.title} protocol I put together for you${who ? ` — ${who}` : ""}. Tap to view:`,
        linkUrl: flyerLink,
        recipients: [{ id: contact.id, phone: contact.phone, first_name: contact.first_name || "" }],
      });
      if (res?.sent) {
        toast.success(`Text sent to ${contact.first_name || contact.phone}`);
        onOpenChange(false);
      } else {
        toast.error(res?.detail || res?.error || "Text not sent");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send text");
    } finally {
      setTexting(false);
    }
  }

  async function send() {
    if (!contact) return toast.error("Pick a customer first");
    setSending(true);
    try {
      const res = await sendEmail({
        user_record_id: recId,
        subject,
        html: flyer.html,
        template_name: `Flyer: ${flyer.title}`,
        recipients: [{ id: contact.id, email: contact.email, first_name: contact.first_name || "", last_name: contact.last_name || "", full_name: `${contact.first_name || ""} ${contact.last_name || ""}`.trim() }],
      });
      if (res?.sent) {
        toast.success(`Flyer sent to ${contact.email}`);
        onOpenChange(false);
      } else {
        toast.error(res?.errors?.[0] || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  }

  async function saveTemplate() {
    setSaving(true);
    try {
      await designCreateTemplate({ user_record_id: recId, title: `Flyer — ${flyer.title}`, subject, content: "", mode: "ai", body_html: flyer.html });
      toast.success("Saved to your Design Templates");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100%-1.5rem)] p-0 overflow-hidden rounded-2xl" data-testid="flyer-email-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-100">
          <DialogTitle className="text-base flex items-center gap-2"><Mail size={16} className="text-violet-600" /> Email this flyer</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-5 max-h-[75vh]">
          <div className="md:col-span-2 p-4 space-y-3 border-b md:border-b-0 md:border-r border-zinc-100 overflow-y-auto">
            <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 rounded-xl" data-testid="flyer-email-subject" />
            <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">Send to a CRM customer</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-2.5 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="h-9 pl-8 rounded-xl" data-testid="flyer-email-search" />
            </div>
            <ul className="max-h-56 overflow-y-auto divide-y divide-zinc-100 rounded-xl border border-zinc-100" data-testid="flyer-email-results">
              {searching && !results.length ? (
                <li className="p-3 text-xs text-zinc-500"><Loader2 size={12} className="animate-spin inline mr-1" />Searching…</li>
              ) : results.length === 0 ? (
                <li className="p-3 text-xs text-zinc-500">No contacts found.</li>
              ) : results.map((u) => {
                const on = contact?.id === u.id;
                return (
                  <li key={u.id}>
                    <button type="button" onClick={() => setContact(u)} className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${on ? "bg-violet-50" : "hover:bg-zinc-50"}`} data-testid={`flyer-email-contact-${u.id}`}>
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium text-zinc-900 truncate">{`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email}</span>
                        <span className="block text-[11px] text-zinc-500 truncate">{u.email}</span>
                      </span>
                      {on && <Check size={14} className="text-violet-600" />}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center gap-2 pt-1">
              <button type="button" onClick={send} disabled={sending || !contact} className="flex-1 h-10 rounded-full bg-[#0b2545] text-white text-xs font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="flyer-email-send">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send flyer
              </button>
              <button type="button" onClick={sendText} disabled={texting || !contact} className="h-10 px-3 rounded-full bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1.5 hover:bg-emerald-700 disabled:opacity-50" data-testid="flyer-sms-send" title={contact?.phone ? `Text ${contact.phone}` : "No phone on file"}>
                {texting ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />} Text
              </button>
              <button type="button" onClick={saveTemplate} disabled={saving} className="h-10 px-3 rounded-full border border-zinc-200 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-zinc-50 disabled:opacity-50" data-testid="flyer-email-save-template" title="Save to Design Templates">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
          <div className="md:col-span-3 bg-zinc-100 p-3 overflow-hidden">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold mb-1.5">Preview · sent with the OreVital header &amp; your footer</div>
            <iframe title="Flyer preview" srcDoc={flyer?.preview_html || ""} className="w-full h-[34vh] md:h-[62vh] bg-white rounded-xl border border-zinc-200" data-testid="flyer-email-preview" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
